import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { money, num, perWatt, STATUS_LABELS, STATUS_TONE } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import ExternalBidDialog from "@/components/ExternalBidDialog";
import { ArrowLeft, Lock, FileText, BarChart3, Archive, RefreshCw, Wrench } from "lucide-react";

// A deliberately slim, read-only view for imported / historical "shadow" bids.
// These records carry a stored $ from the old system but no live scope — so the full
// 13-tab estimating workspace is confusing. This shows exactly what's known, nothing more.
export default function ImportedBidView({ bid }) {
  const navigate = useNavigate();
  const [reviseOpen, setReviseOpen] = useState(false);
  const [converting, setConverting] = useState(false);

  const buildFullEstimate = async () => {
    if (!window.confirm("Price in house: open a NEW revision with the bid's setup only and the full estimating engine on. The sent rounds stay sealed exactly as they are (lump sum, proposal file, dates). Continue?")) return;
    const cd = window.prompt("Client due date for the new round, YYYY-MM-DD or blank", "");
    if (cd === null) return;
    const reason = window.prompt("Reason (goes to the bid activity)", "re-price in house") || "re-price in house";
    setConverting(true);
    try {
      await api.post(`/bids/${bid.bid_id}/versions`, { type: "revision", mode: "in_house", client_due_date: cd.trim() || null, reason });
      toast.success("In-house revision opened — build it out line by line");
      window.location.reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not open the in-house revision"); setConverting(false); }
  };

  const openArchivedPdf = async () => {
    try {
      const res = await api.get(`/bids/${bid.bid_id}/proposal-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("No archived PDF for this record"); }
  };

  const facts = [
    ["Client", bid.client_name || "—"],
    ["State", bid.state || "—"],
    ["Discipline", (bid.discipline_scope || "—").replace(/^\w/, (c) => c.toUpperCase())],
    ["Submitted", (bid.submitted_at || bid.sent_at) ? new Date(bid.submitted_at || bid.sent_at).toLocaleDateString() : "—"],
    ...(bid.external_proposal ? [["Proposal sent", bid.external_proposal.filename || "Attached"]] : []),
  ];

  return (
    <div className="flex flex-col h-full" data-testid="imported-bid-view">
      {/* Slim header */}
      <div className="shrink-0 bg-white border-b border-slate-200">
        <div className="flex items-center gap-4 px-5 py-3">
          <button onClick={() => navigate(-1)} className="text-slate-400 hover:text-slate-700" data-testid="imported-back">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="font-display text-lg font-bold tracking-tight text-slate-900 truncate">{bid.name}</h1>
              <Badge className={`${STATUS_TONE[bid.status]} hover:${STATUS_TONE[bid.status]}`}>{STATUS_LABELS[bid.status] || bid.status}</Badge>
              <span className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-500 bg-slate-100 rounded px-1.5 py-0.5">
                <Archive className="w-3 h-3" /> {bid.external_priced ? "Priced elsewhere" : "Imported"}
              </span>
            </div>
            <div className="text-xs text-slate-500 truncate">{bid.client_name || "No client"} · {bid.location || bid.state || "—"}</div>
          </div>
        </div>
      </div>

      <div className="shrink-0 bg-slate-100 border-b border-slate-200 text-slate-600 text-xs px-5 py-2 flex items-center gap-2" data-testid="imported-readonly-banner">
        <Lock className="w-3.5 h-3.5" />
        {bid.external_priced
          ? "Priced outside GridForge — a tracked lump-sum bid. Revise the lump sum or scope anytime, or build it into a full line-by-line estimate. Figures below power your benchmarks & pricing history."
          : "Imported from your previous system — stored exactly as priced/sent. Read-only; figures below are the stored numbers and power your benchmarks & pricing history."}
      </div>

      {/* Body */}
      <div className="flex-1 overflow-auto min-h-0 p-6">
        <div className="max-w-3xl space-y-5">
          <Card className="p-6" data-testid="imported-summary-card">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
              <div>
                <div className="text-[11px] uppercase tracking-wide text-slate-400">Total (as priced)</div>
                <div className="text-2xl font-mono font-bold text-slate-900 tabular-nums mt-1" data-testid="imported-total">{money(bid.stored_total)}</div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wide text-slate-400">$/Watt</div>
                <div className="text-2xl font-mono font-bold text-slate-900 tabular-nums mt-1">{bid.stored_cost_per_watt ? perWatt(bid.stored_cost_per_watt) : "—"}</div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wide text-slate-400">Capacity</div>
                <div className="text-2xl font-mono font-bold text-slate-900 tabular-nums mt-1">{bid.capacity_mw ? `${num(bid.capacity_mw)} MW` : "—"}</div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wide text-slate-400">Outcome</div>
                <div className="text-2xl font-semibold text-slate-900 mt-1 capitalize">{bid.outcome || bid.status || "—"}</div>
              </div>
            </div>
          </Card>

          <Card className="p-6" data-testid="imported-facts-card">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-600 mb-4">Record details</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
              {facts.map(([k, v]) => (
                <div key={k}>
                  <div className="text-[11px] uppercase tracking-wide text-slate-400">{k}</div>
                  <div className="text-sm font-medium text-slate-800 mt-1 truncate">{v}</div>
                </div>
              ))}
            </div>
            {bid.outcome === "lost" && bid.loss_reason && (
              <div className="mt-5 rounded-lg border border-rose-100 bg-rose-50/60 px-3 py-2 text-xs text-rose-700" data-testid="imported-loss-reason">
                <span className="font-semibold">Loss reason:</span> {bid.loss_reason}
              </div>
            )}
          </Card>

          {Array.isArray(bid.external_history) && bid.external_history.length > 0 && (
            <Card className="p-6" data-testid="imported-price-history-card">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-600 mb-4">Price history</h2>
              <div className="space-y-2">
                {[...bid.external_history].reverse().map((h, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 text-sm border-b border-slate-50 pb-2" data-testid={`price-history-row-${i}`}>
                    <div className="min-w-0">
                      <div className="font-mono font-semibold text-slate-700 tabular-nums">{money(h.total)}</div>
                      <div className="text-[11px] text-slate-400 truncate">{h.at ? new Date(h.at).toLocaleDateString() : "—"}{h.by_name ? ` · ${h.by_name}` : ""}{h.note ? ` · ${h.note}` : ""}</div>
                    </div>
                    <div className="text-right text-xs font-mono text-slate-500 tabular-nums shrink-0">{h.cost_per_watt ? perWatt(h.cost_per_watt) : "—"}{h.capacity_mw ? ` · ${num(h.capacity_mw)} MW` : ""}</div>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between gap-3 text-sm mt-2">
                <div className="min-w-0">
                  <div className="font-mono font-bold text-[#003E87] tabular-nums">{money(bid.stored_total)}</div>
                  <div className="text-[11px] text-emerald-600 font-semibold">Current</div>
                </div>
                <div className="text-right text-xs font-mono text-slate-500 tabular-nums shrink-0">{bid.stored_cost_per_watt ? perWatt(bid.stored_cost_per_watt) : "—"}{bid.capacity_mw ? ` · ${num(bid.capacity_mw)} MW` : ""}</div>
              </div>
            </Card>
          )}

          <div className="flex flex-wrap items-center gap-2">
            {bid.external_priced && (
              <Button onClick={() => setReviseOpen(true)} data-testid="imported-revise" className="bg-[#003E87] hover:bg-[#00306a] text-white">
                <RefreshCw className="w-4 h-4 mr-1.5" /> Revise / update lump sum & scope
              </Button>
            )}
            {(bid.external_priced || bid.imported) && (
              <Button variant="outline" onClick={buildFullEstimate} disabled={converting} data-testid="imported-price-in-house">
                <Wrench className="w-4 h-4 mr-1.5 text-[#003E87]" /> {converting ? "Opening…" : "Price in house (new revision)"}
              </Button>
            )}
            {bid.proposal_pdf_filename && (
              <Button variant="outline" onClick={openArchivedPdf} data-testid="imported-open-pdf">
                <FileText className="w-4 h-4 mr-1.5 text-rose-600" /> Open archived proposal PDF
              </Button>
            )}
            <Button variant="outline" onClick={() => navigate("/analytics")} data-testid="imported-view-history">
              <BarChart3 className="w-4 h-4 mr-1.5 text-[#003E87]" /> View in Bid history
            </Button>
          </div>
        </div>
      </div>

      {bid.external_priced && (
        <ExternalBidDialog open={reviseOpen} onOpenChange={setReviseOpen} editBid={bid}
          onCreated={() => { setReviseOpen(false); window.location.reload(); }} />
      )}
    </div>
  );
}
